import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle2, AlertCircle, Check, ArrowRight, Shield, Clock, Sparkles, X, BarChart3 } from "lucide-react";
import analytics from "@/utils/analytics";
import MaintenanceOverlay from "@/components/MaintenanceOverlay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modern Two-Pane Welcome Modal Component (3rd image style)
const WelcomeModal = ({ profile, onBegin, isVisible }) => {
  const [agreed, setAgreed] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const isMV = profile?.assessmentType === 'manager_validation';
  const displayName = isMV ? (profile?.validatingManagerName || profile?.fullName) : profile?.fullName;
  const employeeName = isMV ? profile?.fullName : null;

  const handleBegin = () => {
    setIsExiting(true);
    setTimeout(() => {
      onBegin();
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isExiting ? 'opacity-0' : 'opacity-100'
        }`}
      />
      
      {/* Modal - Two Pane Design (Like 3rd image) */}
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden transition-all duration-300 ${
          isExiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}
        style={{ animation: isExiting ? 'none' : 'modalFadeIn 0.4s ease-out' }}
        data-testid="welcome-modal"
      >
        <div className="flex flex-col lg:flex-row min-h-[550px]">
          
          {/* Left Pane - Dark Sagicor Blue */}
          <div className="relative lg:w-[45%] bg-gradient-to-br from-[#003366] via-[#00406b] to-[#002040] p-8 lg:p-10 flex flex-col justify-between overflow-hidden">
            {/* Geometric Background */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-64 h-64 bg-[#63C465]/20 rounded-full blur-[80px]" />
              <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
              <div className="absolute top-10 right-10 w-20 h-20 border border-[#63C465]/30 rotate-45 rounded-lg" />
              <div className="absolute bottom-20 left-10 w-24 h-24 border border-[#63C465]/20 -rotate-12 rounded-xl" />
            </div>

            {/* Content */}
            <div className="relative z-10">
              {/* Logo */}
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#63C465] rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-sm">S</span>
                </div>
                <span className="text-xl font-bold text-white">Sagicor</span>
              </div>

              {/* Main Message */}
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 bg-[#63C465]/20 border border-[#63C465]/30 rounded-full px-4 py-1.5">
                  <Sparkles className="w-4 h-4 text-[#63C465]" />
                  <span className="text-sm font-medium text-[#63C465]">Technology Assessment</span>
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
                  {isMV ? (
                    <>Manager <br/><span className="text-[#63C465]">Validation</span></>
                  ) : (
                    <>Shape Your <br/><span className="text-[#63C465]">Career Path</span></>
                  )}
                </h2>
                
                <p className="text-white/70 text-base leading-relaxed">
                  {isMV 
                    ? `Complete the Manager Validation Form for ${employeeName || 'your team member'}.`
                    : "This assessment helps us understand your skills, experience, and career aspirations."
                  }
                </p>
              </div>
            </div>

            {/* Bottom Stats */}
            <div className="relative z-10 flex items-center gap-6 pt-6 border-t border-white/10">
              <div className="flex items-center gap-2 text-white/60">
                <Clock className="w-4 h-4" />
                <span className="text-sm">45-60 mins</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Shield className="w-4 h-4" />
                <span className="text-sm">Secure & Private</span>
              </div>
            </div>
          </div>

          {/* Right Pane - White */}
          <div className="lg:w-[55%] p-8 lg:p-10 flex flex-col bg-white overflow-y-auto max-h-[550px] lg:max-h-[85vh]">
            {/* Welcome Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[#63C465]/10 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#63C465]" />
                </div>
                <span className="text-sm font-semibold text-[#63C465]">
                  Email Verified
                </span>
              </div>
              <h3 className="text-2xl font-bold text-[#3b3b3b]">
                Welcome, {displayName || "Candidate"}
              </h3>
              <p className="text-[#6b7280] mt-1">
                {isMV 
                  ? `You are completing a Manager Validation for ${employeeName || 'your team member'}`
                  : "Here's what you need to know before starting"
                }
              </p>
            </div>

            {/* Info Sections */}
            <div className="space-y-5 flex-1">
              <div className="bg-[#f8f9fa] rounded-xl p-5">
                <h4 className="font-semibold text-[#3b3b3b] mb-2 flex items-center gap-2">
                  <div className="w-6 h-6 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-3.5 h-3.5 text-[#00AEEF]" />
                  </div>
                  Why This Assessment?
                </h4>
                <p className="text-sm text-[#6b7280] leading-relaxed">
                  This is <strong>not a performance review</strong>. It's an investment in your growth to create better training programs and career pathways.
                </p>
              </div>

              <div className="bg-[#f8f9fa] rounded-xl p-5">
                <h4 className="font-semibold text-[#3b3b3b] mb-3 flex items-center gap-2">
                  <div className="w-6 h-6 bg-[#63C465]/10 rounded-lg flex items-center justify-center">
                    <Shield className="w-3.5 h-3.5 text-[#63C465]" />
                  </div>
                  Your Privacy Is Protected
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {["Responses are encrypted", "Never shared externally", "Limited access only", "Used for your growth"].map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-[#6b7280]">
                      <Check className="w-4 h-4 text-[#63C465] flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Action Area */}
            <div className="pt-6 mt-auto border-t border-[#e5e7eb]">
              <div 
                className="flex items-center gap-3 mb-5 cursor-pointer group"
                onClick={() => setAgreed(!agreed)}
              >
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                  agreed 
                    ? 'bg-[#63C465] border-[#63C465]' 
                    : 'border-[#d1d5db] group-hover:border-[#63C465]'
                }`}>
                  {agreed && <Check className="w-4 h-4 text-white" />}
                </div>
                <span className="text-sm text-[#4b5563] select-none">
                  I understand and agree to proceed with the assessment
                </span>
              </div>

              <Button
                onClick={handleBegin}
                disabled={!agreed}
                className={`w-full h-14 rounded-xl font-semibold text-base transition-all duration-300 ${
                  agreed 
                    ? 'bg-[#63C465] hover:bg-[#4fa651] text-white shadow-lg' 
                    : 'bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed'
                }`}
                data-testid="begin-assessment-btn"
              >
                Begin Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <p className="text-xs text-[#9ca3af] text-center mt-4">
                Your progress is automatically saved as you go
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

function LandingPageContent() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [profile, setProfile] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    fetchAssessment();
  }, [token]);

  const fetchAssessment = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/assessment/${token}`);
      setProfile(response.data.profile);
      
      // Start analytics session for candidate
      analytics.startSession({
        type: 'candidate',
        id: response.data.profile?.id,
        email: response.data.profile?.email,
        name: response.data.profile?.fullName,
        progress: response.data.profile?.progressPercentage || 0
      });
      analytics.trackPageView('Assessment Landing Page');
      
      // For TA/HOS assessments, redirect to TALandingPage which has the mic test gate
      const aType = response.data.profile?.assessmentType;
      if (aType === 'technical_assessment' || aType === 'hands_on_simulation') {
        navigate(`/ta-assessment/${token}`, { replace: true });
        return;
      }

      // If user has already started their assessment, go directly to the form
      if (response.data.profile?.status === 'started' || response.data.profile?.status === 'completed') {
        navigate(`/dynamic-assessment/${token}`, { replace: true });
        return;
      }
      
      if (response.data.emailVerified) {
        setEmailVerified(true);
        // Show the welcome modal instead of redirecting immediately
        setShowWelcomeModal(true);
      }
    } catch (err) {
      console.error("Error fetching assessment:", err);
      setError("Assessment not found. Please check your link and try again.");
      analytics.trackError("Assessment not found", err.stack);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    try {
      setVerifying(true);
      const response = await axios.post(`${API}/assessment/${token}/verify`, {
        email: email.trim()
      });

      if (response.data.verified) {
        setEmailVerified(true);
        setShowWelcomeModal(true);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error("Error verifying email:", err);
      toast.error(err.response?.data?.message || "Failed to verify email");
    } finally {
      setVerifying(false);
    }
  };

  const handleBeginAssessment = () => {
    // Track action before navigating
    analytics.trackAction('begin_assessment', { token });
    // Redirect to dynamic assessment form instead of old hardcoded form
    navigate(`/dynamic-assessment/${token}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#00AEEF] mx-auto mb-4" />
          <p className="text-[#6b7280]">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa] p-6">
        <Card className="max-w-md w-full border-[#e5e7eb]">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-red-600">Assessment Not Found</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-[#6b7280] mb-4">
              If you believe this is an error, please contact HR for assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Welcome Modal */}
      <WelcomeModal 
        profile={profile}
        onBegin={handleBeginAssessment}
        isVisible={showWelcomeModal}
      />

      {/* Left Side - Clean Form (2nd image style) */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen bg-[#f8f9fa]">
        {/* Header */}
        <div className="p-8">
          <div className="flex flex-col">
            <span className="text-lg font-bold text-[#3b3b3b]">Technology Assessment Portal</span>
            <span className="text-sm text-[#9ca3af] mt-1">Sagicor Technology Team</span>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-8 pb-8">
          <div className="w-full max-w-sm">
            {emailVerified && !showWelcomeModal ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#63C465]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-[#63C465]" />
                </div>
                <h3 className="text-lg font-semibold text-[#63C465] mb-2">Email Verified!</h3>
                <p className="text-[#6b7280]">Redirecting to your assessment...</p>
              </div>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-[#3b3b3b] mb-2">
                  Welcome
                </h1>
                <p className="text-[#6b7280] mb-8">
                  {profile?.assessmentType === 'manager_validation' 
                    ? `Verify your email to start the Manager Validation for ${profile?.fullName || 'your team member'}`
                    : "Verify your email to start your Technology Assessment"
                  }
                </p>

                {/* Profile Card */}
                <div className="bg-white rounded-xl border border-[#e5e7eb] p-4 mb-6">
                  <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#f3f4f6]">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#00AEEF] to-[#0099d6] rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {(profile?.assessmentType === 'manager_validation' 
                          ? (profile?.validatingManagerName || profile?.fullName) 
                          : profile?.fullName
                        )?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-[#3b3b3b]">
                        {profile?.assessmentType === 'manager_validation' 
                          ? (profile?.validatingManagerName || profile?.fullName || "Manager") 
                          : (profile?.fullName || "Candidate")
                        }
                      </p>
                      <p className="text-sm text-[#6b7280]">
                        {profile?.assessmentType === 'manager_validation' 
                          ? `Completing validation for ${profile?.fullName || "employee"}`
                          : (profile?.jobTitle || "Technology Professional")
                        }
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[#9ca3af] text-xs">Department</p>
                      <p className="text-[#4b5563] font-medium">{profile?.department || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[#9ca3af] text-xs">Location</p>
                      <p className="text-[#4b5563] font-medium">{profile?.location || "—"}</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleVerifyEmail} className="space-y-5">
                  <div>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 bg-white border-[#e5e7eb] rounded-lg text-[#3b3b3b] placeholder:text-[#9ca3af] focus:border-[#00AEEF] focus:ring-[#00AEEF]/20"
                      data-testid="email-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={verifying}
                    className="w-full h-12 bg-[#63C465] hover:bg-[#4fa651] text-white rounded-lg font-medium transition-all"
                    data-testid="verify-email-btn"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Verify & Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-[#9ca3af] text-center mt-6">
                  Need help? Contact <a href="mailto:supportsagicor@thcohq.com" className="text-[#00AEEF] hover:underline">supportsagicor@thcohq.com</a>
                </p>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 pt-0">
          <p className="text-xs text-[#9ca3af] text-center">
            © 2026 THCO
          </p>
        </div>
      </div>

      {/* Right Side - Gradient Design (2nd image style) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Sagicor Blue to Green gradient with shapes */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00AEEF] via-[#00a0dc] to-[#63C465]">
          {/* Geometric Shapes */}
          <div 
            className="absolute -right-20 top-1/4 w-[500px] h-[600px] rounded-3xl transform rotate-12 opacity-30"
            style={{ background: 'linear-gradient(135deg, #ffffff 0%, transparent 100%)' }}
          />
          <div 
            className="absolute -right-10 top-1/3 w-[450px] h-[550px] rounded-3xl transform rotate-6 opacity-20"
            style={{ background: 'linear-gradient(135deg, #ffffff 0%, transparent 100%)' }}
          />
          <div 
            className="absolute right-20 bottom-0 w-[400px] h-[500px] rounded-3xl transform -rotate-12 opacity-25"
            style={{ background: 'linear-gradient(135deg, #ffffff 0%, transparent 100%)' }}
          />
        </div>

        {/* Floating Dashboard Card */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[380px]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-2xl font-bold text-[#3b3b3b]">Assessment</p>
                <p className="text-sm text-[#6b7280]">Technology Capability</p>
              </div>
              <div className="flex items-center gap-1.5 bg-[#63C465]/10 text-[#63C465] px-3 py-1.5 rounded-full text-xs font-medium">
                <Shield className="w-3.5 h-3.5" />
                Secure
              </div>
            </div>

            {/* Chart Area */}
            <div className="bg-gradient-to-b from-[#63C465]/5 to-transparent rounded-xl p-4 mb-4">
              <svg className="w-full h-24" viewBox="0 0 300 80">
                <line x1="0" y1="20" x2="300" y2="20" stroke="#e5e7eb" strokeWidth="1" />
                <line x1="0" y1="40" x2="300" y2="40" stroke="#e5e7eb" strokeWidth="1" />
                <line x1="0" y1="60" x2="300" y2="60" stroke="#e5e7eb" strokeWidth="1" />
                
                <path 
                  d="M 0,60 L 30,55 L 60,45 L 90,30 L 120,25 L 150,35 L 180,30 L 210,40 L 240,25 L 270,30 L 300,20 L 300,80 L 0,80 Z" 
                  fill="url(#sagicorGradient)" 
                  opacity="0.3"
                />
                
                <path 
                  d="M 0,60 L 30,55 L 60,45 L 90,30 L 120,25 L 150,35 L 180,30 L 210,40 L 240,25 L 270,30 L 300,20" 
                  fill="none" 
                  stroke="#63C465" 
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                
                <path 
                  d="M 0,70 L 30,65 L 60,60 L 90,55 L 120,50 L 150,55 L 180,45 L 210,50 L 240,40 L 270,45 L 300,35" 
                  fill="none" 
                  stroke="#00AEEF" 
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                
                <circle cx="120" cy="25" r="4" fill="#63C465" />
                <circle cx="240" cy="25" r="4" fill="#63C465" />
                <circle cx="240" cy="40" r="4" fill="#00AEEF" />
                
                <defs>
                  <linearGradient id="sagicorGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#63C465" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#63C465" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-xl font-bold text-[#3b3b3b]">4</p>
                <p className="text-xs text-[#6b7280]">Sections</p>
              </div>
              <div className="text-center border-x border-[#f3f4f6]">
                <p className="text-xl font-bold text-[#3b3b3b]">69</p>
                <p className="text-xs text-[#6b7280]">Questions</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-[#63C465]">45m</p>
                <p className="text-xs text-[#6b7280]">Est. Time</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export wrapped with MaintenanceOverlay
export default function LandingPage() {
  const { token } = useParams();
  
  return (
    <MaintenanceOverlay assessmentToken={token}>
      <LandingPageContent />
    </MaintenanceOverlay>
  );
}
